import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import { blogApi, type BlogPost } from '../api/blogApi'

/**
 * BlogPostPage — individual post template for a migrated Greystone post.
 *
 * The critical SEO requirement (S3 / D13) is that the original Yoast title and
 * meta description survive the migration. We reapply them to document.title and
 * the <meta name="description"> tag on load so the 301'd old URLs keep ranking.
 */

const CATEGORY_LABEL: Record<string, string> = {
  'home-loans': 'Home Loans',
  refinancing: 'Refinancing',
  business: 'Business',
  'market-updates': 'Market Updates',
}

function formatDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setLoading(true)
    setError(null)
    blogApi
      .getPost(slug)
      .then((data) => {
        if (cancelled) return
        setPost(data)
      })
      .catch(() => {
        if (!cancelled) setError('We couldn’t find that post.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  return (
    <>
      {post && (
        <Seo
          title={post.yoast_title || post.title}
          description={
            post.yoast_meta_description || `Read ${post.title} on the ${tenantConfig.name} blog.`
          }
          path={`/blog/${slug}`}
          type="article"
        />
      )}
      <Nav />
      <main id="main" className="pt-36 pb-20">
        <article className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="text-sm text-gs-text-soft hover:text-ink">
            ← Back to all posts
          </Link>

          {loading && <p className="text-gs-text-soft mt-8">Loading…</p>}
          {error && !loading && (
            <div className="mt-8">
              <h1 className="font-heading text-3xl text-ink mb-2">Post not found</h1>
              <p className="text-gs-text-soft">{error}</p>
            </div>
          )}

          {post && !loading && (
            <>
              <header className="mt-6 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  {post.category && (
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                      style={{ background: '#F4F4F0', color: '#4A4A44' }}
                    >
                      {CATEGORY_LABEL[post.category] ?? 'Insights'}
                    </span>
                  )}
                  {post.published_at && (
                    <span className="text-xs text-gs-text-muted">{formatDate(post.published_at)}</span>
                  )}
                </div>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-ink font-semibold leading-tight">
                  {post.title}
                </h1>
              </header>

              <div
                className="blog-content max-w-none text-gs-text leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: post.body }}
              />
            </>
          )}
        </article>
      </main>
      <Footer />
    </>
  )
}
